import {
  EMPTY_POSITION_FORM_VALUES,
  type PositionFieldName,
} from './constants';
import { getErrorMessage, handleHrisApiError } from '@/utils/errors';
import type { CreatePositionFormValues, KomboEnvelope } from './types';

/** Job title prefills from the Kula job and stays editable. */
export const buildInitialPositionValues = (
  jobTitle?: string | null
): CreatePositionFormValues => ({
  ...EMPTY_POSITION_FORM_VALUES,
  job_posting_title: jobTitle || '',
});

/**
 * Trims the free-text title and drops anything left empty, so Workday sees an
 * absent value rather than an empty reference id (which it rejects outright).
 * Only Team and Job title are required, so the drop is a guard rather than a normal
 * path — it earns its keep if a field is ever relaxed to optional.
 */
export const toCreatePositionPayload = (
  values: CreatePositionFormValues
): Partial<CreatePositionFormValues> =>
  (Object.keys(values) as PositionFieldName[]).reduce<
    Partial<CreatePositionFormValues>
  >((payload, key) => {
    const value = values[key]?.trim?.() ?? values[key];
    if (value) payload[key] = value;
    return payload;
  }, {});

const GENERIC_CREATE_ERROR =
  'Could not create the position in Workday. Try again, or contact support@kula.ai.';

/**
 * Pulls the message to show for a failed `create_position`.
 *
 * A 422 carries `details` — Workday's own wording, which is usually directly
 * actionable ("The Company is required based on…") — so it wins over anything
 * this app could phrase. Codes fall through to the shared integrations error
 * module, the same path `useHrisIntegrations` uses for every other HRIS call.
 */
export const getCreatePositionError = (error: unknown): string => {
  const body = (error as { response?: { data?: KomboEnvelope<unknown> } })
    ?.response?.data as KomboEnvelope<unknown> | undefined;

  const envelope = body ?? (error as KomboEnvelope<unknown>);

  if (envelope?.details) return envelope.details;
  if (envelope?.errors?.length) {
    return (
      getErrorMessage(envelope, 'integrations', handleHrisApiError) ||
      GENERIC_CREATE_ERROR
    );
  }
  return GENERIC_CREATE_ERROR;
};

/**
 * A 2xx envelope can still carry `errors`; `handleApiResponse` in
 * `useHrisIntegrations` treats that as a failure and so does this.
 */
export const hasEnvelopeErrors = (envelope?: KomboEnvelope<unknown>) =>
  !!envelope?.errors?.length;
